import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalHeader } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Pressable } from '@cbhq/cds-web/system';
import { TextBody, TextHeadline, TextLegal } from '@cbhq/cds-web/typography';

import { useProject } from ':cloud/contexts/ProjectContext';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useCreateBaseL3 } from ':cloud/queries/Base/useCreateBaseL3';
import { useGetPaymentMethod } from ':cloud/queries/BillingQueries/useGetPaymentMethod';
import { QueryKey } from ':cloud/queries/types';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { getCardMedia } from ':cloud/utils/billing';
import { AppRoute } from ':cloud/utils/routes';

import { PaymentMethodIntake } from '../billing/PaymentMethodIntake';

type BaseL3DeploymentSummaryItemProps = {
  label: string;
};

function BaseL3DeploymentSummaryItem({ label }: BaseL3DeploymentSummaryItemProps) {
  return (
    <HStack alignItems="center" gap={2}>
      <Icon name="checkmark" color="positive" size="s" />
      <TextBody as="p">{label}</TextBody>
    </HStack>
  );
}

const DEFAULT_ITEMS = [
  'L3 Testnet',
  'Settled on Base L2 with S3',
  'Automated transaction ordering to ensure optimal performance',
  'Managed nodes',
  'Out-of-the-box block explorer',
];

type BaseL3DeploymentSidebarProps = {
  onSubmit?: () => void;
  rollupName: string;
  stack?: string;
  network?: string;
  dataOption?: string;
  tokenAddress?: string;
  isCustomTokenEnabled?: boolean;
  isCustomTokenValidated?: boolean;
};

export function BaseL3DeploymentSidebar({
  rollupName,
  network,
  tokenAddress,
  isCustomTokenEnabled,
  isCustomTokenValidated,
}: BaseL3DeploymentSidebarProps) {
  const history = useHistory();
  const { selectedProject } = useProject();
  const { activeOrg } = useGetUser();
  const orgId = activeOrg?.organizationId;
  const { paymentMethod } = useGetPaymentMethod(orgId);
  const [
    addPaymentMethodModalVisible,
    { toggleOn: showAddPaymentMethodModal, toggleOff: hideAddPaymentMethodModal },
  ] = useToggler();

  const toast = useToast();
  const queryClient = useQueryClient();

  const handleSuccess = useCallback(() => {
    history.push(AppRoute.Products.BaseL3);
  }, [history]);

  const createBaseL3Mutation = useCreateBaseL3({
    orgId,
    projectId: selectedProject?.id,
  });

  const handleStripeSuccess = useCallback(async () => {
    toast.show('Payment method successfully added.', {
      variant: 'positive',
    });
    await queryClient.invalidateQueries({ queryKey: [QueryKey.billing, 'paymentMethod', orgId] });
    await queryClient.invalidateQueries({ queryKey: [QueryKey.accountCredits, orgId] });
    hideAddPaymentMethodModal();
  }, [toast, queryClient, orgId, hideAddPaymentMethodModal]);

  const cardMedia = useMemo(
    () => getCardMedia(paymentMethod?.card?.network, <Icon name="card" size="m" />),
    [paymentMethod],
  );

  const handleStripeError = useCallback(
    (intent) => {
      if (intent.last_setup_error?.message) {
        toast.show(intent.last_setup_error.message, {
          variant: 'negative',
        });
      }
    },
    [toast],
  );

  const handleStripePaymentMethodError = useCallback(
    (errorMessage: string | undefined) => {
      if (errorMessage) {
        toast.show(errorMessage, {
          variant: 'negative',
        });
      }
    },
    [toast],
  );

  const hasMissingFields = !rollupName || (!isCustomTokenValidated && isCustomTokenEnabled);
  const isDisabled = !paymentMethod || hasMissingFields;

  const handleSubmit = useCallback(() => {
    createBaseL3Mutation.mutate(
      {
        name: rollupName,
        network: network === 'Testnet' ? 'base-sepolia' : 'base',
        type: network === 'Testnet' ? 'testnet' : 'mainnet',
        customGasToken: isCustomTokenEnabled ? tokenAddress : '',
      },
      { onSuccess: handleSuccess },
    );
    logClick(EventName.base_l3_deploy_click);
  }, [
    createBaseL3Mutation,
    handleSuccess,
    isCustomTokenEnabled,
    network,
    rollupName,
    tokenAddress,
  ]);

  const buttonMessage = useMemo(() => {
    if (hasMissingFields) {
      return 'Complete the required fields to continue';
    }

    if (!paymentMethod) {
      return 'Add a payment method to continue';
    }

    return 'The deployment may take up to 15 minutes';
  }, [hasMissingFields, paymentMethod]);

  const paymentMethodDetails = useMemo(() => {
    if (paymentMethod?.card?.lastFour) {
      return paymentMethod?.card?.lastFour;
    }

    if (paymentMethod?.type === 'sepa_debit') {
      return paymentMethod?.sepaDebit?.lastFour;
    }

    return '';
  }, [paymentMethod]);

  return (
    <VStack gap={6}>
      {addPaymentMethodModalVisible && (
        <Modal
          visible
          onRequestClose={hideAddPaymentMethodModal}
          width={600}
          shouldCloseOnEscPress={false}
        >
          <ModalHeader title="Add payment method" />
          <ModalBody>
            <VStack spacingBottom={5}>
              <PaymentMethodIntake
                onCancel={hideAddPaymentMethodModal}
                returnRoute={AppRoute.Products.BaseL3Deploy}
                parentComponentName="Billing"
                actionsInline
                onSuccess={handleStripeSuccess}
                onRequiresPaymentMethod={handleStripeError}
                onError={handleStripePaymentMethodError}
              />
            </VStack>
          </ModalBody>
        </Modal>
      )}
      <VStack gap={2} borderedBottom spacingBottom={6}>
        <TextHeadline as="h1" spacingBottom={2}>
          Summary
        </TextHeadline>
        {DEFAULT_ITEMS.map((item) => (
          <BaseL3DeploymentSummaryItem key={item} label={item} />
        ))}
      </VStack>
      <VStack gap={2}>
        <HStack justifyContent="space-between" alignItems="center">
          <TextBody as="p" color="foregroundMuted">
            Plan
          </TextBody>
          <TextBody as="p">$1/ month</TextBody>
        </HStack>
        <HStack justifyContent="space-between" alignItems="center">
          <TextBody as="p" color="foregroundMuted">
            Deployment fee
          </TextBody>
          <TextBody as="p">Free</TextBody>
        </HStack>
        <HStack justifyContent="space-between" alignItems="center" spacingBottom={2}>
          <TextBody as="p" color="foregroundMuted">
            Pay with
          </TextBody>
          {paymentMethod ? (
            <HStack alignItems="center" gap={1}>
              {cardMedia}
              <TextBody as="p">{`****${paymentMethodDetails}`}</TextBody>
            </HStack>
          ) : (
            <Pressable background="transparent" onPress={showAddPaymentMethodModal}>
              <TextHeadline as="p" color="primary">
                Add payment method
              </TextHeadline>
            </Pressable>
          )}
        </HStack>
        <Button onPress={handleSubmit} disabled={isDisabled}>
          Pay and deploy
        </Button>
        <TextLegal as="p" color="foregroundMuted">
          {buttonMessage}
        </TextLegal>
      </VStack>
    </VStack>
  );
}
